import { Instance, Instances } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { Color, MathUtils, Vector3 } from "three";

const spellHitcolor = new Color("#7FE3FA");

interface AnimatedBoxProps {
  scale: number;
  target: Vector3;
  speed: number;
}

/**
 * Creating instances for the explosion particles
 */
const AnimatedBox = ({ scale, target, speed }: AnimatedBoxProps) => {
  const ref = useRef<any>();
  useFrame((_, delta) => {
    if (ref.current!.scale.x > 0) {
      ref.current!.scale.x =
        ref.current!.scale.y =
        ref.current!.scale.z -=
        speed * delta;
    }
    ref.current!.position.lerp(target, speed);
  });
  return <Instance ref={ref} scale={scale} position={[0, 0, 0]} />;
};

interface SpellHitProps {
  nb: number;
  position: Vector3;
  onHitEnded: () => void;
}

/**
 * Generating a number of small spheres with a random target position as particles for an explosion-like effect
 */
const SpellHit = ({ nb = 30, position, onHitEnded }: SpellHitProps) => {
  const spheres = useMemo(
    () =>
      Array.from({ length: nb }, () => ({
        target: new Vector3(
          MathUtils.randFloat(-0.6, 0.6),
          MathUtils.randFloat(-0.6, 0.6),
          MathUtils.randFloat(-0.6, 0.6)
        ),
        scale: 0.1, //MathUtils.randFloat(0.03, 0.09),
        speed: MathUtils.randFloat(0.1, 0.3),
      })),
    [nb]
  );

  useEffect(() => {
    setTimeout(() => {
      onHitEnded();
    }, 500);
  }, [onHitEnded]);

  return (
    <group position={[position.x, position.y, position.z]}>
      <Instances>
        <sphereGeometry />
        <meshStandardMaterial toneMapped={false} color={spellHitcolor} />
        {spheres.map((sphere, i) => (
          <AnimatedBox key={i} {...sphere} />
        ))}
      </Instances>
    </group>
  );
};

export default SpellHit;
